import React, { useEffect, useRef } from 'react';

interface UseKeyboardNavigationOptions {
  enabled?: boolean;
  onEscape?: () => void;
  onEnter?: () => void;
  onArrowUp?: () => void;
  onArrowDown?: () => void;
  onArrowLeft?: () => void;
  onArrowRight?: () => void;
  trapFocus?: boolean;
}

export function useKeyboardNavigation<T extends HTMLElement = HTMLDivElement>({
  enabled = true,
  onEscape,
  onEnter,
  onArrowUp,
  onArrowDown,
  onArrowLeft,
  onArrowRight,
  trapFocus = false,
}: UseKeyboardNavigationOptions = {}): React.RefObject<T> {
  const ref = useRef<T>(null);
  const firstFocusableElementRef = useRef<HTMLElement | null>(null);
  const lastFocusableElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!enabled || !ref.current) { return; }

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          onEscape?.();
          break;
        case 'Enter':
          if (event.target === ref.current) {
            event.preventDefault();
            onEnter?.();
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          onArrowUp?.();
          break;
        case 'ArrowDown':
          event.preventDefault();
          onArrowDown?.();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          onArrowLeft?.();
          break;
        case 'ArrowRight':
          event.preventDefault();
          onArrowRight?.();
          break;
        case 'Tab':
          if (trapFocus && ref.current) {
            handleTabKeyPress(event);
          }
          break;
        default:
          break;
      }
    };

    const handleTabKeyPress = (event: KeyboardEvent) => {
      if (!ref.current) { return; }

      const focusableElements = ref.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) { return; }

      firstFocusableElementRef.current = focusableElements[0] as HTMLElement;
      lastFocusableElementRef.current = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (event.shiftKey) {
        if (document.activeElement === firstFocusableElementRef.current) {
          event.preventDefault();
          lastFocusableElementRef.current?.focus();
        }
      } else {
        if (document.activeElement === lastFocusableElementRef.current) {
          event.preventDefault();
          firstFocusableElementRef.current?.focus();
        }
      }
    };

    const element = ref.current;
    element.addEventListener('keydown', handleKeyDown);

    return () => {
      element.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, onEscape, onEnter, onArrowUp, onArrowDown, onArrowLeft, onArrowRight, trapFocus]);

  return ref;
}

// Hook for managing focus within a dropdown or modal
export function useFocusTrap<T extends HTMLElement = HTMLDivElement>(enabled: boolean = true): React.RefObject<T> {
  return useKeyboardNavigation<T>({ enabled, trapFocus: true });
}

// Hook for accessible dropdown menus
export function useDropdownNavigation<T extends HTMLElement = HTMLDivElement>(
  items: unknown[],
  onSelect: (index: number) => void,
  onClose: () => void,
  isOpen: boolean = true
): [React.RefObject<T>, number, (index: number) => void] {
  const ref = useRef<T>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(-1);

  const handleArrowDown = () => {
    setSelectedIndex(prev => (prev + 1) % items.length);
  };

  const handleArrowUp = () => {
    setSelectedIndex(prev => (prev - 1 + items.length) % items.length);
  };

  const handleEnter = () => {
    if (selectedIndex >= 0) {
      onSelect(selectedIndex);
    }
  };

  const handleEscape = () => {
    onClose();
  };

  useKeyboardNavigation<T>({
    enabled: isOpen,
    onArrowDown: handleArrowDown,
    onArrowUp: handleArrowUp,
    onEnter: handleEnter,
    onEscape: handleEscape,
    trapFocus: true,
  });

  // Reset selected index when dropdown closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedIndex(-1);
    }
  }, [isOpen]);

  return [ref, selectedIndex, setSelectedIndex];
}